import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { RequestVirtualCardScreen } from 'src/app/RequestVirtualCard/RequestVirtualCard'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { UserAssociation } from 'src/models/user.models'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { buildLowLimitAlert } from 'src/utils/lowLimitAlert.utils'

interface RequestVirtualCardRecurringContentProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  postVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostVirtualCardRequest>,
  ) => void
  setScreen: (val: RequestVirtualCardScreen) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
}

const RequestVirtualCardRecurringContent: React.FC<
  RequestVirtualCardRecurringContentProps
> = (props) => {
  const cardTypeRecurringImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeRecurring,
  )
  const { analytics } = useAnalyticsContext()
  const lowBalanceAlertFeatureEnabled = !!useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_low_balance_alert

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    recurrenceFields: RecurrenceFormValues,
  ): void => {
    props.postVirtualCardAndOpenToastAndCloseFlyout({
      cardholder:
        flatFields.userEmails !== undefined
          ? flatFields.userEmails[0]
          : undefined,
      displayName: flatFields.displayName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : 0,
      currency: flatFields.currency,
      notes: flatFields.notes,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      recurs: true,
      recurrence: {
        // TODO: maybe don't submit 0 here?
        balanceCents:
          flatFields.balanceDollars !== undefined
            ? toCents(flatFields.balanceDollars)
            : 0,
        period: recurrenceFields.period,
        interval: recurrenceFields.interval,
        terminator: recurrenceFields.terminator,
        count:
          recurrenceFields.terminator === RecurrenceTerminator.Count
            ? recurrenceFields.count
            : undefined,
        until:
          recurrenceFields.terminator === RecurrenceTerminator.Date
            ? recurrenceFields.untilMoment?.format('YYYY-MM-DD')
            : undefined,
        byWeekDay:
          recurrenceFields.period === RecurrencePeriod.Weekly
            ? recurrenceFields.byWeekDay
            : undefined,
        byMonthDay:
          recurrenceFields.period === RecurrencePeriod.Monthly
            ? recurrenceFields.byMonthDay
            : undefined,
        byYearDay:
          recurrenceFields.period === RecurrencePeriod.Yearly
            ? recurrenceFields.byYearDay
            : undefined,
      },
      lowLimitAlert: buildLowLimitAlert(
        flatFields,
        lowBalanceAlertFeatureEnabled,
      ),
    })

    analytics?.track('Successfully request virtual card', {
      type_of_card: 'recurring',
      card_name: flatFields.displayName,
      credit_limit: flatFields.balanceDollars,
      add_expense_category: Object.keys(expenseDetails).length > 0,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      add_attachments:
        props.receiptAttachments && props.receiptAttachments.length > 0,
      add_notes: !!flatFields.notes,
      number_recurrences:
        recurrenceFields.count && recurrenceFields.count > 0
          ? recurrenceFields.count
          : undefined,
      recurrence_period: recurrenceFields.period,
    })
  }

  return (
    <ActionRightFlyoutContent
      title={<Trns source={'Request a Virtual Card'} />}
      specificElements={{
        subtitle: <Trns source={'Recurring'} />,
        imageSrc: cardTypeRecurringImage,
        changeLink: {
          ariaLabelSource: 'Change Card Type',
          changeMessage: <Trns source={'Change Card Type'} />,
          onChangeClick: (): void => {
            props.setScreen(RequestVirtualCardScreen.Pick)
          },
        },
      }}
    >
      <VirtualCardForm
        isRecurringPayment
        buttonText={<Trns source={'Request Virtual Card'} />}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.postingVirtualCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={<Trns source={'Request From'} />}
        recipientNamesEditable={false}
        recurrenceEditable
        notesEditable
        amountProps={{
          amountEditable: true,
        }}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        attachmentsProps={{
          receiptAttachments: props.receiptAttachments,
          uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
          uploadAttachments: props.uploadAttachments,
          deleteAttachment: props.deleteAttachment,
        }}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        suggestedProps={{
          showSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.virtualCardFrom]}
        isRequest
        id={'request'}
        unableToAddNewUser
        typeofCard={VirtualCardTypes.recurring}
      />
    </ActionRightFlyoutContent>
  )
}

export default RequestVirtualCardRecurringContent
